/* Native cart-note saving. No app hooks, cart-line changes, or API monkeypatches. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else api.install(root, root.document);
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';

  function createController(options) {
    let value = options.initialNote || '';
    let revision = 0;
    let verifiedRevision = 0;
    let phase = 'saved';
    let message = '';
    let active = null;
    let slow = false;
    const listeners = new Set();
    const schedule = options.setTimer || setTimeout;
    const cancel = options.clearTimer || clearTimeout;

    function state() {
      const verified = revision === verifiedRevision && phase === 'saved';
      return { value, revision, phase, message, slow, verified, blocked: !verified };
    }
    function emit() {
      for (const listener of listeners) listener(state());
    }
    function edit(next) {
      if (next === value) return;
      value = next;
      revision += 1;
      if (!active) phase = 'dirty';
      message = '';
      emit();
    }
    function save() {
      if (active) return active;
      if (state().verified) return Promise.resolve(true);
      phase = 'saving';
      slow = false;
      message = '';
      // Start in a microtask so every listener sees the same in-flight promise.
      active = Promise.resolve().then(async function () {
        try {
          while (true) {
            const snapshot = { value, revision };
            const timer = schedule(function () {
              slow = true;
              emit();
            }, options.slowAfterMs || 15000);
            try {
              const result = await options.saveNote(snapshot.value);
              if (!result || !Object.prototype.hasOwnProperty.call(result, 'note') ||
                  !(result.note === snapshot.value || (snapshot.value === '' && result.note === null))) {
                throw new Error('The server did not confirm the exact gift message.');
              }
            } finally {
              cancel(timer);
            }
            verifiedRevision = snapshot.revision;
            slow = false;
            if (revision === snapshot.revision) {
              phase = 'saved';
              return true;
            }
            // Older text finished successfully. Only now send the latest edit.
          }
        } catch (error) {
          phase = 'error';
          message = 'We could not confirm your gift message was saved. Your text is still here. Please try Save again.';
          return false;
        } finally {
          active = null;
          slow = false;
          emit();
        }
      });
      emit();
      return active;
    }
    return {
      state, edit, save,
      subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); }
    };
  }

  function createTransport(fetcher, updateUrl) {
    return async function (note) {
      let response;
      try {
        response = await fetcher(updateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note })
        });
      } catch (_) {
        throw transportError('network');
      }
      if (!response.ok) throw transportError('http', response.status);
      try {
        return await response.json();
      } catch (_) {
        throw transportError('invalid_json', response.status);
      }
    };
  }

  function transportError(category, status) {
    const error = new Error('Gift message save failed: ' + category + '.');
    error.category = category;
    if (Number.isInteger(status) && status >= 100 && status <= 599) error.status = status;
    return error;
  }

  function needsGate(current, kind) {
    return kind === 'express' ? current.blocked || current.value !== '' : current.blocked;
  }

  function install(win, doc) {
    if (win.PaliGiftMessage) return win.PaliGiftMessage;
    const script = doc.currentScript || doc.querySelector('script[data-pali-gift-message-script]');
    const config = script ? script.dataset : {};
    const fieldSelector = '[data-pali-gift-note-input]';
    const wrapperSelector = '[data-pali-gift-note]';
    const initialFields = Array.from(doc.querySelectorAll(fieldSelector));
    const initialField = initialFields[0];
    const initialNote = Object.prototype.hasOwnProperty.call(config, 'initialNote')
      ? config.initialNote : (initialField ? initialField.defaultValue : '');
    const rootPath = win.Shopify && win.Shopify.routes ? win.Shopify.routes.root : '/';
    const updateUrl = (config.updateUrl || rootPath + 'cart/update.js').replace(/\/cart\/update$/, '/cart/update.js');
    const cartUrl = config.cartUrl || rootPath + 'cart';
    const transport = createTransport(win.fetch.bind(win), updateUrl);
    const controller = createController({
      initialNote,
      async saveNote(note) {
        try {
          const result = await transport(note);
          if (!result || !Object.prototype.hasOwnProperty.call(result, 'note') ||
              !(result.note === note || (note === '' && result.note === null))) {
            throw transportError('note_mismatch');
          }
          return result;
        } catch (error) {
          // Never log request/response bodies, the note, or arbitrary error text.
          const category = ['network', 'http', 'invalid_json', 'note_mismatch'].includes(error.category)
            ? error.category : 'unexpected';
          const diagnostic = { category };
          if (Number.isInteger(error.status) && error.status >= 100 && error.status <= 599) {
            diagnostic.status = error.status;
          }
          if (win.console && typeof win.console.warn === 'function') {
            win.console.warn('[Pali gift message save] ' + JSON.stringify(diagnostic));
          }
          throw error;
        }
      }
    });
    // A customer or browser may edit/restore a textarea before this deferred script runs.
    // The current value is not a server-verified baseline.
    const editedFields = initialFields.filter(function (input) { return input.value !== input.defaultValue; });
    const restoredField = editedFields.find(function (input) { return input === doc.activeElement; }) || editedFields[0];
    let restoredValues = Array.from(new Set(editedFields.map(function (input) { return input.value; })));
    let restoreConflict = restoredValues.length > 1;
    if (restoredField) controller.edit(restoredField.value);
    const acceleratedSelector = '.additional-checkout-buttons, .shopify-payment-button';
    const appInputSelector = '#effectiveAppsEGW, #effectiveAppsEGWATC';
    const appControlSelector = appInputSelector +
      ', label[for="effectiveAppsEGW"], label[for="effectiveAppsEGWATC"]';
    const gated = new Map();
    let pendingCheckout = null;
    let bypassForm = null;
    let checkoutError = '';

    function text(element, value) {
      if (element && element.textContent !== value) element.textContent = value;
    }
    function status(current) {
      if (checkoutError) return checkoutError;
      if (restoreConflict) return 'Different gift-message drafts were restored. Review them below, then click Save to keep the message in the field.';
      if (current.phase === 'error') return current.message;
      if (current.slow) return 'Saving is taking longer than expected. Keep this page open; do not retry yet.';
      if (current.phase === 'saving') return 'Saving gift message…';
      if (current.phase === 'dirty') return 'Your changes are not saved yet.';
      return current.value ? 'Gift message saved.' : 'No gift message saved.';
    }
    function gate(element, kind) {
      if (gated.has(element)) {
        const previous = gated.get(element);
        if (previous.message) text(previous.message, gateMessage(kind));
        return;
      }
      let original = null;
      try {
        original = JSON.parse(element.getAttribute('data-pali-note-original'));
      } catch (_) { /* New elements normally have no preserved gate state. */ }
      const savedAttributes = original || {
        inert: element.getAttribute('inert'),
        aria: element.getAttribute('aria-disabled'),
        hidden: element.getAttribute('hidden'),
        disabled: 'disabled' in element ? element.disabled : null
      };
      const previous = { ...savedAttributes, kind, notice: doc.createElement('p'), message: doc.createElement('span') };
      element.setAttribute('data-pali-note-original', JSON.stringify(savedAttributes));
      // Some app/theme section refreshes clone current markup, including our gate.
      const sibling = element.nextElementSibling;
      if (sibling && sibling.hasAttribute('data-pali-note-gate-notice')) sibling.remove();
      previous.notice.setAttribute('data-pali-note-gate-notice', '');
      previous.notice.setAttribute('class', 'pali-gift-note-gate');
      previous.notice.setAttribute('role', 'status');
      previous.message.textContent = gateMessage(kind);
      previous.notice.appendChild(previous.message);
      if (kind === 'express') {
        const link = doc.createElement('a');
        link.href = cartUrl;
        link.textContent = ' Review your cart.';
        previous.notice.appendChild(link);
        // Hiding also prevents iframe payment buttons from bypassing event handlers.
        element.setAttribute('hidden', '');
      }
      element.setAttribute('inert', '');
      element.setAttribute('aria-disabled', 'true');
      element.setAttribute('data-pali-note-gated', kind);
      if (previous.disabled !== null) element.disabled = true;
      element.insertAdjacentElement('afterend', previous.notice);
      gated.set(element, previous);
    }
    function gateMessage(kind) {
      if (kind === 'app') return 'Save your gift message before changing gift bag options.';
      return controller.state().verified && !restoreConflict
        ? 'For orders with a gift message, use the regular checkout button.'
        : 'Save your gift message before checkout. Express checkout is unavailable while a gift message is present.';
    }
    function restore(element, previous) {
      for (const [attribute, old] of [['inert', previous.inert], ['aria-disabled', previous.aria],
        ['hidden', previous.hidden]]) {
        if (old === null) element.removeAttribute(attribute);
        else element.setAttribute(attribute, old);
      }
      if (previous.disabled !== null) element.disabled = previous.disabled;
      element.removeAttribute('data-pali-note-gated');
      element.removeAttribute('data-pali-note-original');
      previous.notice.remove();
    }
    function render() {
      const current = controller.state();
      if (restoreConflict) current.blocked = true;
      for (const input of doc.querySelectorAll(fieldSelector)) {
        // New section HTML must not replace a newer in-memory draft or saved value.
        if (input.value !== current.value) input.value = current.value;
      }
      for (const wrapper of doc.querySelectorAll(wrapperSelector)) {
        wrapper.setAttribute('data-state', current.phase);
        wrapper.setAttribute('aria-busy', current.phase === 'saving' ? 'true' : 'false');
        text(wrapper.querySelector('[data-pali-gift-note-status]'), status(current));
        const button = wrapper.querySelector('[data-pali-gift-note-save]');
        if (button) button.disabled = current.phase === 'saving' || (current.verified && !restoreConflict);
        let review = wrapper.querySelector('[data-pali-note-restored-drafts]');
        if (restoreConflict && !review) {
          review = doc.createElement('details');
          review.setAttribute('data-pali-note-restored-drafts', '');
          review.open = true;
          const summary = doc.createElement('summary');
          summary.textContent = 'Review restored drafts';
          review.appendChild(summary);
          for (const [index, value] of restoredValues.entries()) {
            const draft = doc.createElement('pre');
            draft.textContent = 'Restored draft ' + (index + 1) + ':\n' + value;
            review.appendChild(draft);
          }
          wrapper.appendChild(review);
        } else if (!restoreConflict && review) {
          review.remove();
        }
      }
      for (const [element, previous] of gated) {
        if (!element.isConnected || !needsGate(current, previous.kind)) {
          restore(element, previous);
          gated.delete(element);
        }
      }
      if (needsGate(current, 'express')) {
        for (const element of doc.querySelectorAll(acceleratedSelector)) gate(element, 'express');
      }
      if (current.blocked) {
        for (const input of doc.querySelectorAll(appInputSelector)) {
          // Gate the label as a whole, keeping its checkbox and explanatory text together.
          const label = doc.querySelector('label[for="' + input.id + '"]');
          gate(label || input, 'app');
          if (label && !gated.has(input)) {
            let original = null;
            try { original = JSON.parse(input.getAttribute('data-pali-note-original')); } catch (_) {}
            const savedAttributes = original || { inert: input.getAttribute('inert'), aria: input.getAttribute('aria-disabled'),
              hidden: input.getAttribute('hidden'), disabled: input.disabled };
            const previous = { ...savedAttributes, kind: 'app', notice: doc.createElement('span') };
            input.setAttribute('data-pali-note-original', JSON.stringify(savedAttributes));
            input.disabled = true;
            input.setAttribute('inert', '');
            gated.set(input, previous);
          }
        }
      }
    }
    function showError(message) {
      checkoutError = message;
      render();
      const inputs = Array.from(doc.querySelectorAll(fieldSelector));
      const visible = function (input) {
        return typeof input.getClientRects !== 'function' || input.getClientRects().length > 0;
      };
      const input = inputs.find(function (candidate) { return candidate === doc.activeElement && visible(candidate); }) ||
        inputs.find(visible) || inputs[0];
      if (input) {
        const details = input.closest('details');
        if (details) details.open = true;
        input.focus();
      }
    }
    function isCartForm(form) {
      return form && (form.id === 'cart' || form.id === 'CartDrawer-Form');
    }
    function resolveSubmitter(form, original) {
      if (!original) return null;
      if (original.isConnected && original.form === form) return original;
      if (original.id) {
        const replacement = doc.getElementById(original.id);
        if (replacement && replacement.form === form) return replacement;
      }
      return Array.from(form.elements || []).find(function (element) {
        return element.name === original.name && element.value === original.value &&
          (element.type === 'submit' || element.type === 'image');
      }) || null;
    }
    doc.addEventListener('input', function (event) {
      if (!event.target.matches || !event.target.matches(fieldSelector)) return;
      checkoutError = '';
      controller.edit(event.target.value);
    }, true);
    doc.addEventListener('click', function (event) {
      const target = event.target.closest ? event.target : event.target.parentElement;
      if (!target) return;
      const save = target.closest('[data-pali-gift-note-save]');
      if (save) {
        event.preventDefault();
        checkoutError = '';
        restoreConflict = false;
        restoredValues = [];
        controller.save();
        render();
      } else if ((needsGate(controller.state(), 'express') && target.closest(acceleratedSelector)) ||
          (controller.state().blocked && target.closest(appControlSelector))) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showError(controller.state().verified
          ? 'For orders with a gift message, use the regular checkout button.'
          : 'Save your gift message before continuing.');
      }
    }, true);
    doc.addEventListener('submit', function (event) {
      const form = event.target;
      if (!isCartForm(form)) return;
      render();
      if (restoreConflict) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showError('Review the restored gift-message drafts, then click Save before checkout.');
        return;
      }
      if (bypassForm === form && controller.state().verified) {
        bypassForm = null;
        return;
      }
      if (controller.state().verified) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pendingCheckout) return;
      const formId = form.id;
      const originalSubmitter = event.submitter;
      checkoutError = '';
      pendingCheckout = controller.save().then(function (success) {
        if (!success || !controller.state().verified) {
          if (success) showError('Your gift message changed. Save it before continuing.');
          else showError(controller.state().message);
          return;
        }
        const currentForm = doc.getElementById(formId);
        const submitter = currentForm && resolveSubmitter(currentForm, originalSubmitter);
        if (!currentForm || typeof currentForm.requestSubmit !== 'function' ||
            (originalSubmitter && !submitter) || (submitter && submitter.disabled)) {
          showError('Your gift message is saved. Please use the checkout button again.');
          return;
        }
        bypassForm = currentForm;
        try {
          if (submitter) currentForm.requestSubmit(submitter);
          else currentForm.requestSubmit();
        } finally {
          bypassForm = null;
        }
      }).catch(function () {
        showError(controller.state().verified
          ? 'Your gift message is saved. Please use the checkout button again.'
          : 'Your gift message has not been confirmed saved. Please try Save again.');
      }).finally(function () {
        pendingCheckout = null;
      });
    }, true);
    win.addEventListener('beforeunload', function (event) {
      if (!controller.state().blocked && !restoreConflict) return;
      event.preventDefault();
      event.returnValue = '';
    });
    controller.subscribe(render);
    const observer = new win.MutationObserver(render);
    observer.observe(doc.documentElement, { childList: true, subtree: true });
    // A tiny scoped rule is required because some theme styles override [hidden].
    const style = doc.createElement('style');
    style.textContent = '[data-pali-note-gated="express"]{display:none!important}';
    doc.head.appendChild(style);
    render();
    win.PaliGiftMessage = controller;
    return controller;
  }
  return { createController, createTransport, needsGate, install };
});
